import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getPatreAlumnos, getPatre, createPatreRegistro, 
  bulkUploadPatre, downloadPatreTemplate 
} from '../lib/api';
import { formatNombreCompleto } from '../lib/nombre';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, CalendarBlank, Upload, Download, BookOpen, Warning, 
  Student, Check, X, Question
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const ESTADOS_PATRE = [
  { value: 'trabajo', label: 'Trabajó', color: 'bg-green-100 text-green-800' },
  { value: 'no_trabajo', label: 'No trabajó', color: 'bg-red-100 text-red-800' },
  { value: 'sin_material', label: 'Sin material', color: 'bg-amber-100 text-amber-800' },
  { value: 'indisciplinado', label: 'Indisciplinado', color: 'bg-red-100 text-red-800' },
  { value: 'asistio_participo', label: 'Asistió y participó', color: 'bg-green-100 text-green-800' },
  { value: 'entrego_actividad', label: 'Entregó actividad', color: 'bg-green-100 text-green-800' },
  { value: 'entrego_incompleto', label: 'Entregó incompleto', color: 'bg-amber-100 text-amber-800' },
  { value: 'falto', label: 'Faltó', color: 'bg-gray-100 text-gray-800' },
  { value: 'requiere_apoyo', label: 'Requiere apoyo', color: 'bg-blue-100 text-blue-800' },
  { value: 'requiere_seguimiento', label: 'Requiere seguimiento', color: 'bg-purple-100 text-purple-800' },
  { value: 'sin_novedad', label: 'Sin novedad', color: 'bg-gray-100 text-gray-800' }
];

const PatrePage = () => {
  const { user, hasPermission } = useAuth();
  const [alumnos, setAlumnos] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  const [selectedAlumno, setSelectedAlumno] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('captura');

  const [formData, setFormData] = useState({
    materia: '',
    estado: '',
    observaciones: '',
    estrategia_aplicada: '',
    compromiso_alumno: '',
    compromiso_tutor: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [alumnosRes, registrosRes] = await Promise.all([
        getPatreAlumnos(),
        getPatre({ fecha: format(date, 'yyyy-MM-dd') })
      ]);
      
      setAlumnos(alumnosRes.data);
      setRegistros(registrosRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAlumno) return;
    
    try {
      await createPatreRegistro({
        alumno_id: selectedAlumno.id,
        matricula: selectedAlumno.matricula,
        fecha: format(date, 'yyyy-MM-dd'),
        ...formData
      });
      toast.success('Registro guardado');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const response = await bulkUploadPatre(file);
      toast.success(`${response.data.updated} alumnos actualizados en PATRE`);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchData();
    } catch (error) {
      toast.error('Error al cargar archivo');
    }
    e.target.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadPatreTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_patre.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const openCaptureModal = (alumno) => {
    setSelectedAlumno(alumno);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      materia: '',
      estado: '',
      observaciones: '',
      estrategia_aplicada: '',
      compromiso_alumno: '',
      compromiso_tutor: ''
    });
  };

  const getEstadoData = (estado) => {
    return ESTADOS_PATRE.find(e => e.value === estado) || { label: estado, color: 'bg-gray-100 text-gray-800' };
  };

  const getAlumnoRegistros = (alumnoId) => {
    return registros.filter(r => r.alumno_id === alumnoId);
  };

  const materias = user?.materias || ['Matemáticas', 'Español', 'Ciencias', 'Historia', 'Geografía', 'Inglés', 'Educación Física', 'Artes'];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patre-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Programa PATRE</h1>
          <p className="text-muted-foreground">Seguimiento de alumnos en riesgo académico</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start" data-testid="date-picker">
                <CalendarBlank size={16} className="mr-2" />
                {format(date, "d 'de' MMMM, yyyy", { locale: es })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                locale={es}
              />
            </PopoverContent>
          </Popover>
          
          {hasPermission(['administrador']) && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDownloadTemplate}
              >
                <Download size={16} className="mr-1" />
                Plantilla
              </Button>
              
              <label>
                <Button variant="outline" size="sm" asChild>
                  <span>
                    <Upload size={16} className="mr-1" />
                    Cargar PATRE
                  </span>
                </Button>
                <input 
                  type="file" 
                  accept=".xlsx,.xls" 
                  className="hidden" 
                  onChange={handleUpload}
                />
              </label>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-amber-50 border-amber-200 dark:bg-amber-900/20">
          <CardContent className="p-4 flex items-center gap-3">
            <Warning size={24} className="text-amber-600" weight="duotone" />
            <div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{alumnos.length}</p>
              <p className="text-sm text-amber-600 dark:text-amber-400">Total en PATRE</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <BookOpen size={24} className="text-primary" weight="duotone" />
            <div>
              <p className="text-2xl font-bold">{registros.length}</p>
              <p className="text-sm text-muted-foreground">Registros hoy</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="captura">Captura Diaria</TabsTrigger>
          <TabsTrigger value="alumnos">Lista de Alumnos</TabsTrigger>
        </TabsList>

        <TabsContent value="captura" className="mt-4">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <BookOpen size={20} />
                Captura PATRE - {format(date, "EEEE d 'de' MMMM", { locale: es })}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="w-full">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[100px]">Matrícula</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead className="w-[80px]">Grado</TableHead>
                      <TableHead className="w-[80px]">Grupo</TableHead>
                      <TableHead>Materias en Riesgo</TableHead>
                      <TableHead>Registros Hoy</TableHead>
                      <TableHead className="w-[100px] text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="spinner mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : alumnos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          <Student size={40} className="mx-auto mb-2 opacity-50" />
                          No hay alumnos en el programa PATRE
                        </TableCell>
                      </TableRow>
                    ) : (
                      alumnos.map((alumno) => {
                        const alumnoRegistros = getAlumnoRegistros(alumno.id);
                        return (
                          <TableRow key={alumno.id} className="hover:bg-muted/30">
                            <TableCell className="font-mono text-sm">{alumno.matricula}</TableCell>
                            <TableCell className="font-medium min-w-[160px] max-w-[240px]">
                              <span
                                className="text-xs sm:text-sm md:text-base break-words whitespace-normal leading-tight block"
                                title={formatNombreCompleto(alumno)}
                              >
                                {formatNombreCompleto(alumno)}
                              </span>
                            </TableCell>
                            <TableCell>{alumno.grado}°</TableCell>
                            <TableCell>{alumno.grupo}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {alumno.materias_riesgo?.slice(0, 3).map((m, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {m}
                                  </Badge>
                                ))}
                                {alumno.materias_riesgo?.length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{alumno.materias_riesgo.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {alumnoRegistros.slice(0, 3).map((r) => (
                                  <Badge key={r.id || `${r.alumno_id}-${r.materia}`} className={`text-xs ${getEstadoData(r.estado).color}`}>
                                    {r.materia?.slice(0, 3)}
                                  </Badge>
                                ))}
                                {alumnoRegistros.length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{alumnoRegistros.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {hasPermission(['administrador', 'direccion', 'docente']) && (
                                <Button
                                  size="sm"
                                  onClick={() => openCaptureModal(alumno)}
                                  data-testid={`capture-${alumno.matricula}`}
                                >
                                  <Plus size={16} className="mr-1" />
                                  Capturar
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alumnos" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {alumnos.map((alumno, index) => (
              <Card 
                key={alumno.id} 
                className="card-hover stagger-item"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <CardTitle className="text-sm sm:text-base md:text-lg font-medium break-words leading-tight">
                        {formatNombreCompleto(alumno)}
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Mat: {alumno.matricula} | {alumno.grado}° {alumno.grupo}
                      </p>
                    </div>
                    <Warning size={20} className="text-amber-500" weight="fill" />
                  </div>
                </CardHeader>
                <CardContent>
                  {alumno.motivo_patre && (
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground">Motivo de riesgo</p>
                      <p className="text-sm">{alumno.motivo_patre}</p>
                    </div>
                  )}
                  {alumno.materias_riesgo?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground mb-1">Materias en riesgo</p>
                      <div className="flex flex-wrap gap-1">
                        {alumno.materias_riesgo.map((m) => (
                          <Badge key={m} variant="secondary" className="text-xs">
                            {m}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {alumno.observaciones_patre && (
                    <div>
                      <p className="text-xs text-muted-foreground">Observaciones</p>
                      <p className="text-sm line-clamp-2">{alumno.observaciones_patre}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Capture Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-sm sm:text-base md:text-lg break-words leading-tight">
              Captura PATRE - {formatNombreCompleto(selectedAlumno)}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Materia *</Label>
                <Select 
                  value={formData.materia} 
                  onValueChange={(v) => setFormData(p => ({ ...p, materia: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {materias.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado *</Label>
                <Select 
                  value={formData.estado} 
                  onValueChange={(v) => setFormData(p => ({ ...p, estado: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_PATRE.map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Observaciones</Label>
              <Textarea
                value={formData.observaciones}
                onChange={(e) => setFormData(p => ({ ...p, observaciones: e.target.value }))}
                placeholder="Observaciones sobre el alumno..."
                rows={2}
              />
            </div>
            <div>
              <Label>Estrategia Aplicada</Label>
              <Textarea
                value={formData.estrategia_aplicada}
                onChange={(e) => setFormData(p => ({ ...p, estrategia_aplicada: e.target.value }))}
                placeholder="Estrategia pedagógica aplicada..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Compromiso del Alumno</Label>
                <Input
                  value={formData.compromiso_alumno}
                  onChange={(e) => setFormData(p => ({ ...p, compromiso_alumno: e.target.value }))}
                  placeholder="Compromiso establecido..."
                />
              </div>
              <div>
                <Label>Compromiso del Tutor</Label>
                <Input
                  value={formData.compromiso_tutor}
                  onChange={(e) => setFormData(p => ({ ...p, compromiso_tutor: e.target.value }))}
                  placeholder="Compromiso del tutor..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!formData.materia || !formData.estado}>
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatrePage;
